<script setup lang="ts">
import { whenever } from '@vueuse/core';
import { nextTick, ref, watch } from 'vue';
import { useStore } from 'vuex';

import { AlgoliaVariant } from '@/api/algolia';
import Input from '@/components/base/form/Input.vue';
import TextArea from '@/components/base/form/TextArea.vue';
import Modal from '@/components/base/layout/Modal.vue';
import TextButton from '@/components/base/TextButton.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header4 from '@/components/base/typography/Header4.vue';
import Header6 from '@/components/base/typography/Header6.vue';
import QuantitySelector from '@/components/cart/QuantitySelector.vue';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import money from '@/filters/money';
import { GreetingCardCartLineItemFields, NutsLineItem } from '@/lib/cart/lineItem';
import {
  buildAddGreetingCardAction,
  buildUpdateGreetingCardAction as buildReplaceGreetingCardAction,
  findMessage,
  isGreetingCardLineItem,
} from '@/utils/cart';
import { ProductCard } from '@/utils/productCard';

export interface Props {
  lineItem?: NutsLineItem<GreetingCardCartLineItemFields>;
  options: AlgoliaVariant[];
}
const props = defineProps<Props>();

export type Models = {
  isOpen: boolean;
  step: 'select' | 'edit';
};
const isOpen = defineModel<Models['isOpen']>('isOpen');
const step = defineModel<Models['step']>('step', {
  required: true,
});

const emit = defineEmits<{ 'new-line-added': [] }>();

const messageCharacterLimit = 300;
const selections = ref({
  greetingCardName: '',
  image: '',
  message: '',
  quantity: 1,
  senderName: '',
  sku: '',
});
const simplifyName = (name: string) => name.replace(/^Greeting Card: /, '').trim();

watch(
  () => props.lineItem,
  (lineItem) => {
    if (lineItem && isGreetingCardLineItem(lineItem)) {
      const messageAndSender = findMessage(lineItem)?.split('\n - ') ?? [];
      selections.value = {
        greetingCardName: simplifyName(lineItem.name.en),
        image: lineItem.titleImage?.small ?? '',
        message: messageAndSender?.[0] ?? '',
        quantity: lineItem.quantity,
        senderName: messageAndSender?.[1] ?? '',
        sku: lineItem.variant.sku,
      };
    } else {
      selections.value = {
        greetingCardName: '',
        image: '',
        message: '',
        quantity: 1,
        senderName: '',
        sku: '',
      };
    }
  },
);

const emojiRegex = /[^\p{L}\p{N}\p{P}\p{Z}~`+=|<>\n]/gu;
const cleanseEmojis = (message: string) => message.replace(emojiRegex, '');
whenever(
  () => [selections.value.message, selections.value.senderName].map((val) => emojiRegex.test(val)),
  async ([stripMessage, stripSenderName]) => {
    // necessary to make sure sub-components sync up properly, for some reason
    await nextTick();
    if (stripMessage) selections.value.message = cleanseEmojis(selections.value.message);
    if (stripSenderName) selections.value.senderName = cleanseEmojis(selections.value.senderName);
  },
);

/** Refer to `auto_scale_image.php` for variable `$PERMITTED_SIZES` */
const nudgeImageUrlResolutionParam = (card: AlgoliaVariant) =>
  card.Product.listingImageUrl.replace('228x152fill', '510x340');

const simplifyOptionName = (greetingCard: AlgoliaVariant) =>
  simplifyName(greetingCard.Product.name);

const selectGreetingCard = (card: AlgoliaVariant) => {
  selections.value = {
    ...selections.value,
    greetingCardName: simplifyOptionName(card),
    image: nudgeImageUrlResolutionParam(card),
    sku: card.sku,
  };
  step.value = 'edit';
};

const error = ref<string>();
const form = ref<HTMLFormElement>();
const validated = ref(false);
watch(
  () => selections.value.message,
  (value) => {
    error.value = validated.value && !value ? 'Please enter a value' : undefined;
  },
);
whenever(
  () => step.value === 'edit',
  () => {
    validated.value = false;
    error.value = undefined;
  },
);

const matchesLineItem = (message: string, quantity: number, sku: string) =>
  !!props.lineItem &&
  message === findMessage(props.lineItem) &&
  quantity === props.lineItem.quantity &&
  sku === props.lineItem.variant.sku;
const { addToCart, updateCart, updateLineItem } = useCart(useStore());
const submit = useCallback(async () => {
  if (!selections.value.sku) return;
  if (!selections.value.message) {
    validated.value = true;
    error.value = 'Please enter a value';
    form.value?.querySelector('textarea')?.focus();
    return;
  }
  const { message: messageBody, quantity, senderName, sku } = selections.value;
  const message = senderName ? `${messageBody}\n - ${senderName}` : messageBody ?? '';

  if (props.lineItem) {
    if (matchesLineItem(message, quantity, sku)) {
      isOpen.value = false;
      return;
    }
    if (props.lineItem.variant.sku === sku) {
      await updateLineItem(props.lineItem.id, {
        greetingCardMessage: message,
        quantity,
      });
    } else {
      await updateCart(() =>
        buildReplaceGreetingCardAction(props.lineItem, sku, message, quantity),
      );
    }
  } else {
    const addAction = buildAddGreetingCardAction(message, quantity, sku);
    await addToCart(
      {
        cart_sku: {
          custom: {
            key: addAction.custom!.type.key!, // we expect this to be defined
            fields: addAction.custom!.fields!, // we expect this to be defined
          },
          quantity,
          sku_external_id: sku,
        },
      },
      {
        postAddToCartCallback: () => emit('new-line-added'),
        skipCartAdded: true,
      },
    );
  }
  isOpen.value = false;
});

const highlightOption = ref<string>();
</script>

<template>
  <Modal
    aria-label="greeting card selections"
    data-test="greeting-card-modal"
    :hasControls="false"
    :isOpen
    width="md:max-w-2xl"
    @handle-close="isOpen = false"
  >
    <template #header>
      <Transition name="fade" appear>
        <Header4 v-if="!lineItem" data-test="greeting-card-modal-header">
          Add A Greeting Card
        </Header4>
        <Header4 v-else data-test="greeting-card-modal-header">Edit Greeting Card</Header4>
      </Transition>
    </template>

    <template #body>
      <Transition name="fade" mode="out-in" appear>
        <div
          v-if="step === 'select'"
          class="grid grid-cols-2 justify-items-center mt-5 overflow-x-hidden overflow-y-auto h-[60vh] sm:grid-cols-3 auto-rows-min gap-4 md:gap-6"
          data-test="greeting-card-options"
        >
          <div v-for="greetingCard in options" class="grow" :key="greetingCard.sku">
            <div
              class="w-full overflow-hidden smooth-transition min-h-28"
              :class="{ shadow: highlightOption === greetingCard.sku }"
            >
              <img
                alt="Greeting card image"
                class="object-contain w-full smooth-transition min-h-28"
                :class="{ 'scale-105': highlightOption === greetingCard.sku }"
                data-test="greeting-card-option-image"
                :src="nudgeImageUrlResolutionParam(greetingCard)"
              />
            </div>
            <ThemedButton
              theme="white"
              class="mt-2"
              data-test="select-greeting-card-option"
              fullWidth
              @blur="highlightOption = undefined"
              @click="selectGreetingCard(greetingCard)"
              @focus="highlightOption = greetingCard.sku"
              @mouseleave="highlightOption = undefined"
              @mouseover="highlightOption = greetingCard.sku"
            >
              Add
            </ThemedButton>
            <Header6
              class="mt-2 font-bold text-left"
              data-test="greeting-card-option-name"
              headerTag="p"
            >
              {{ simplifyOptionName(greetingCard) }}
            </Header6>
            <Header6 class="mt-1 text-left" data-test="greeting-card-option-price" headerTag="p">
              {{ money(ProductCard.fromAlgolia(greetingCard).piecePrice) }}
            </Header6>
          </div>
        </div>
        <form
          v-else
          class="flex flex-col pt-6"
          data-test="greeting-card-form"
          ref="form"
          @submit.prevent="submit.execute"
        >
          <div class="flex w-full border border-solid border-neutral-300">
            <div class="w-40 h-full shrink-0" data-test="greeting-card-image">
              <img :alt="selections.greetingCardName" class="object-fill" :src="selections.image" />
            </div>
            <div class="flex flex-col items-start justify-between p-3 grow">
              <BaseBodyText class="font-semibold" data-test="greeting-card-name">
                Card Selected: {{ selections.greetingCardName }}
              </BaseBodyText>
              <TextButton
                class="hover:text-nuts-cyan-800 focus:text-nuts-cyan-800 smooth-transition"
                data-test="change-greeting-card-design"
                @click="step = 'select'"
              >
                Change Design
              </TextButton>
            </div>
          </div>

          <QuantitySelector
            v-model="selections.quantity"
            class="mt-4"
            data-test="greeting-card-quantity-selector"
          />

          <TextArea
            v-model="selections.message"
            class="mt-4"
            data-test="gift-message-textarea"
            :errorMessage="error"
            id="gift-message-textarea"
            label="Greeting Card Message"
            labelSROnly
            :maxLength="messageCharacterLimit"
            name="sender-message"
            placeholder="Enter your gift message here"
          />

          <Input
            v-model="selections.senderName"
            aria-label="From"
            class="mt-1"
            data-test="sender-name"
            id="sender-name"
            name="sender-name"
            placeholder="From"
            showPlaceholder
            type="text"
          />

          <ThemedButton
            class="self-end mt-4"
            data-test="save-greeting-card-button"
            :disabled="!selections.sku || !!error"
            :isLoading="submit.isPending"
            theme="gray"
            type="submit"
          >
            Save
          </ThemedButton>
        </form>
      </Transition>
    </template>
  </Modal>
</template>

<style scoped>
.smooth-transition {
  @apply transition duration-500 ease-in-out;
}
</style>
